import React from 'react';
import { useController } from 'react-hook-form';

/**
 * @typedef {import('../../../../types/location').LocationOption} LocationOption
 */

import LocationSearchCombobox from '../../../Shared/SearchCombobox/LocationSearchCombobox';
import { FIELDS } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormField } from './hooks';

/**
 * A self-contained location search field that:
 * - Uses useController for custom component integration
 * - Provides location autocomplete via LocationSearchCombobox
 * - Displays its own error state
 *
 * @param {Object} props
 * @param {(location: LocationOption|null) => void} [props.onLocationSelected] - Callback when location changes
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
export default function LocationField({ onLocationSelected, disabled }) {
  const { control, errorMessage } = useFormField(FIELDS.locationCity, disabled);

  // useController is the hook version of Controller - better for custom components
  const {
    field: { value, onChange, ref },
  } = useController({
    name: FIELDS.locationCity,
    control,
  });

  /**
   * Handle location selection from combobox
   * @param {LocationOption|null} location
   */
  const handleLocationChange = (location) => {
    // Update form with city name
    onChange(location?.city || '');

    // Treat null/undefined and empty string as equivalent (no location selected)
    const locationChanged = (location?.city || '') !== (value || '');

    // Only trigger callback if selected city actually changed
    if (locationChanged) {
      onLocationSelected?.(location);
    }
  };

  return (
    <FieldWrapper label="Location" errorMessage={errorMessage}>
      <LocationSearchCombobox
        name={FIELDS.locationCity}
        value={value}
        onChange={handleLocationChange}
        placeholder="Provider's location"
        showError={!!errorMessage}
        ref={ref}
        disabled={disabled}
      />
    </FieldWrapper>
  );
}
